/*
 * Copyright (c) 2023 CalAmp Corp. All Rights Reserved
 */
import React, {useCallback, useMemo, useState} from 'react';
import {makeStyles} from '@material-ui/core';
import isNil from 'lodash.isnil';
import {translate} from '@ion/core/src/locale/locale';
import {AugmentedSmarterTrailerSensorData, SensorTypes} from '@ion/core/src/types/entities';
import Accordion, {getAccordionWrapperStyles} from '@ion/dls/src/components/accordion/Accordion';
import {AccordionSectionHeader} from '@ion/dls/src/components/accordion/current/AccordionSectionHeader';
import {AssetDetailsSectionAccordion} from '@ion/dls/src/components/assetdetails/current/AssetDetailsSectionAccordion';
import {TombstoneLoader} from '@ion/dls/src/components/loading/TombstoneLoader';
import {useThemeContext} from '@ion/dls/src/theme/useThemeContext';
import {DLSTheme} from '@ion/dls/src/types/types';
import SensorBreadcrumbs from './SensorBreadcrumbs';
import SensorWidgetContainer from './SensorWidgetContainer';
import SmarterTrailerSensorDetails from './SmarterTrailerSensorDetails';

export interface SensorSummaryAccordionProps {
  smarterTrailer: AugmentedSmarterTrailerSensorData,
  loading: boolean,
  onChange?: (expanded: boolean) => void;
  onViewTireSensorReport: Function | null;
  // the current theme accordion's controlled expanded state, driven by the
  // Asset Details Drawer's expand/collapse-all control ...
  expanded?: boolean;
  onExpand?: (expanded: boolean) => void;
};

const useStyles = makeStyles((theme: DLSTheme) => {
  return {
    content : {
      maxWidth       : 330,
      display        : 'flex',
      flexWrap       : 'wrap',
      justifyContent : 'space-evenly'
    },
    detailTombstone : {
      height : 48
    },
    smarterTrailerWidgets : {
      padding : (selectedSensor) => selectedSensor ? '0 15px 0 0' : '0 15px'
    },
    ...getAccordionWrapperStyles()
  };
});

/**
 * Defines the Sensor Summary Accordion component.
 *
 * ```typescript
 *
 * import SensorSummaryAccordion from '@ion/features/src/smarterTrailer';
 *
 * <SensorSummaryAccordion smarterTrailer={smarterTrailer} />
 *
 * ```
 */
const SensorSummaryAccordion = ({
  smarterTrailer,
  loading,
  onChange,
  onViewTireSensorReport,
  expanded,
  onExpand
}: SensorSummaryAccordionProps) => {
  const [selectedSensor, setSelectedSensor] = useState<SensorTypes>(null);
  const classes                             = useStyles({selectedSensor});
  const {isCurrent}                         = useThemeContext();

  const onBreadcrumbClick = useCallback(function(e: React.MouseEvent) {
    e.stopPropagation();
    e.preventDefault();
    setSelectedSensor(null);
  }, []);

  const sensorLabels = useMemo(function() {
    return {
      [SensorTypes.TPMS]       : translate('common:smarterTrailer.tires.label'),
      [SensorTypes.WETS]       : translate('common:smarterTrailer.hubs.label'),
      [SensorTypes.DoorState]  : translate('common:smarterTrailer.door.label'),
      [SensorTypes.CargoSense] : translate('common:smarterTrailer.cargoSense.breadcrumbLabel'),
      [SensorTypes.BMS]        : translate('common:smarterTrailer.brakeChamber.breadcrumbLabel'),
      [SensorTypes.WMS]        : translate('common:smarterTrailer.weightSensor.label'),
      [SensorTypes.LMS]        : translate('common:smarterTrailer.lightOutDetection.breadcrumbLabel')
    };
  }, []);

  const details = isNil(selectedSensor) ? (
    <SensorWidgetContainer setSelectedSensor={setSelectedSensor}
      smarterTrailer={smarterTrailer}
      loading={loading}/>
  ) : (
    <SmarterTrailerSensorDetails selectedSensor={selectedSensor}
      onViewTireSensorReport={onViewTireSensorReport}
      smarterTrailer={smarterTrailer}
      loading={loading} />
  );

  let result;
  if (!loading && isCurrent) {
    result = (
      <AssetDetailsSectionAccordion title={translate('common:smarterTrailer.title')}
        header={isNil(selectedSensor) ? (
          <AccordionSectionHeader title={translate('common:smarterTrailer.title')} />
        ) : (
          <SensorBreadcrumbs label={sensorLabels?.[selectedSensor]}
            selectedSensor={selectedSensor}
            onBackClick={onBreadcrumbClick} />
        )}
        expanded={expanded}
        onExpand={function(isExpanded: boolean) {
          onChange?.(isExpanded);
          onExpand?.(isExpanded);
        }}
        defaultExpanded>
        {details}
      </AssetDetailsSectionAccordion>
    );
  } else if (!loading) {
    result = (
      <div className={classes.accordionWrapper}>
        <Accordion className={classes.smarterTrailerWidgets}
          onExpand={onChange}
          header={
            <SensorBreadcrumbs label={sensorLabels?.[selectedSensor]}
              selectedSensor={selectedSensor}
              onBackClick={onBreadcrumbClick} />
          }
          details={details}
          padding={selectedSensor ? '0px' : '15px 0'}
          borderBottom={'none'}
          isCondensedVariant
          defaultExpanded />
      </div>
    );
  } else {
    result = (
      <div className={classes.detailTombstone}>
        <TombstoneLoader />
      </div>
    );
  }

  return result;
};

export default SensorSummaryAccordion;
